import React, { useState } from 'react';
import { RefreshCw, Info, Zap, Award } from 'lucide-react';
import { motion } from 'framer-motion';

interface LighthouseResult {
  lighthouseResult?: {
    categories?: {
      performance?: {
        score: number;
      };
    };
  };
}

interface PageSpeedBoxProps {
  mobileScore: number;
  desktopScore: number;
  isLoading?: boolean;
  onRefresh?: () => void;
  pagespeedData?: {
    performance_score: number;
    accessibility_score: number;
    best_practices_score: number;
    seo_score: number;
    mobile_performance_score?: number;
    desktop_performance_score?: number;
    mobile_data?: LighthouseResult;
    desktop_data?: LighthouseResult;
  } | null;
  prospectSector?: string;
}

const PageSpeedBox: React.FC<PageSpeedBoxProps> = ({ 
  mobileScore, 
  desktopScore, 
  isLoading = false,
  onRefresh,
  pagespeedData,
  prospectSector
}) => {
  const getScoreColor = (score: number): string => {
    if (score > 70) return '#10b981'; // Vert pour > 70
    if (score >= 61) return '#f59e0b'; // Orange pour 61-70
    return '#ef4444'; // Rouge pour < 60
  };

  const getScoreGaugeColor = (score: number): string => {
    if (score > 70) return '#10b981'; // Vert pour > 70
    if (score >= 61) return '#f59e0b'; // Orange pour 61-70
    return '#ef4444'; // Rouge pour < 60
  };

  const [showTooltip, setShowTooltip] = useState(false);

  // Fonction d'extraction de score simplifiée et robuste
  const extractScore = (scoreValue: number | null | undefined, rawData: LighthouseResult | null | undefined, fallbackScore: number): number => {
    // 1. Utiliser le score spécifique s'il existe
    if (scoreValue !== undefined && scoreValue !== null) {
      return Math.round(scoreValue * 100);
    }
    
    // 2. Extraire le score des données brutes si elles existent
    if (rawData && 
        rawData.lighthouseResult && 
        rawData.lighthouseResult.categories && 
        rawData.lighthouseResult.categories.performance && 
        typeof rawData.lighthouseResult.categories.performance.score === 'number') {
      return Math.round(rawData.lighthouseResult.categories.performance.score * 100);
    }
    
    // 3. Fallback sur le score par défaut
    return fallbackScore;
  };
  
  // Utiliser la fonction pour extraire les scores mobile et desktop
  let mobilePerfScore = mobileScore;
  let desktopPerfScore = desktopScore;
  
  if (pagespeedData) {
    // Extraire les scores en utilisant les données disponibles
    mobilePerfScore = extractScore(
      pagespeedData.mobile_performance_score,
      pagespeedData.mobile_data,
      pagespeedData.performance_score ? Math.round(pagespeedData.performance_score * 100) : mobileScore
    );
    
    desktopPerfScore = extractScore(
      pagespeedData.desktop_performance_score,
      pagespeedData.desktop_data,
      pagespeedData.performance_score ? Math.round(pagespeedData.performance_score * 100) : desktopScore
    );
  }

  if (isLoading) {
    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex justify-center items-center h-40 transition-all duration-300 hover:shadow-md"
      >
        <motion.div 
          className="flex flex-col items-center"
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
            className="mb-3 h-10 w-10 flex items-center justify-center rounded-full"
            style={{ backgroundColor: '#fef3e2', color: '#eb811c' }}
          >
            <RefreshCw size={24} />
          </motion.div>
          <div className="text-sm font-medium" style={{ color: '#1f1f1f' }}>Analyse en cours...</div>
          <div className="w-48 mt-3">
            <div className="overflow-hidden h-1.5 bg-gray-200 rounded-full">
              <motion.div 
                className="h-full rounded-full"
                style={{ backgroundColor: '#eb811c' }}
                initial={{ width: '0%' }}
                animate={{ width: '90%' }}
                transition={{ duration: 5 }}
              />
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 transition-all duration-300 hover:shadow-md h-full flex flex-col justify-between"
    >
      <div className="flex flex-col space-y-4 flex-grow">
        {/* En-tête avec actions */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-end gap-2 sm:gap-0">
          {onRefresh && (
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onRefresh}
              className="text-xs flex items-center px-3 py-1 rounded-full transition-colors"
              style={{ color: '#eb811c', backgroundColor: '#fef3e2' }}
            >
              <RefreshCw className="w-3 h-3 mr-1" /> Actualiser
            </motion.button>
          )}
        </div>
        
        {/* Scores avec animations */}
        <div className="grid grid-cols-2 gap-4 bg-white p-3 rounded-lg shadow-sm">
          <motion.div 
            className="flex flex-col items-center"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <div className="mb-1 text-sm" style={{ color: '#6b7280' }}>Mobile</div>
            <motion.div 
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3 }}
              className="text-2xl font-bold"
              style={{ color: getScoreColor(mobilePerfScore) }}
            >
              {mobilePerfScore}/100
            </motion.div>
            <div className="mt-2 w-full px-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${mobilePerfScore}%` }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                  className="h-2.5 rounded-full"
                  style={{ backgroundColor: getScoreGaugeColor(mobilePerfScore) }}
                ></motion.div>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            className="flex flex-col items-center"
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <div className="mb-1 text-sm" style={{ color: '#6b7280' }}>Desktop</div>
            <motion.div 
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5 }}
              className="text-2xl font-bold"
              style={{ color: getScoreColor(desktopPerfScore) }}
            >
              {desktopPerfScore}/100
            </motion.div>
            <div className="mt-2 w-full px-4">
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <motion.div 
                  initial={{ width: 0 }}
                  animate={{ width: `${desktopPerfScore}%` }}
                  transition={{ delay: 0.6, duration: 0.8 }}
                  className="h-2.5 rounded-full"
                  style={{ backgroundColor: getScoreGaugeColor(desktopPerfScore) }}
                ></motion.div>
              </div>
            </div>
          </motion.div>
        </div>
        
        {/* Message d'interprétation IA - Benchmark sectoriel avec animation */}
        {prospectSector && (
          <motion.div 
            className="w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.7, duration: 0.4 }}
          >
            <div className="text-center text-xs sm:text-sm font-medium p-3 rounded-md border-l-4 shadow-inner"
                 style={{ backgroundColor: '#fef3e2', borderColor: '#eb811c' }}>
              Ce score {desktopPerfScore >= 80 
                ? <><span className="font-bold" style={{ color: '#eb811c' }}>est comparable</span> à la moyenne des sites analysés</> 
                : <>est <span className="font-bold" style={{ color: '#ef4444' }}>inférieur de 20%</span> à la moyenne des sites analysés</>
              } dans le secteur <span style={{ color: '#0f0f0f' }}>{prospectSector}</span>.
              {desktopPerfScore < 80 && <><br/><span style={{ color: '#eb811c' }}>Améliorez votre vitesse de chargement</span> pour une meilleure expérience utilisateur et un meilleur référencement !</>}
            </div>
          </motion.div>
        )}
      
        {/* Lien contextuel avec tooltip amélioré */}
        <div className="text-center relative">
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="text-xs flex items-center justify-center mx-auto"
            style={{ color: '#eb811c' }}
            onClick={() => setShowTooltip(!showTooltip)}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <Info className="w-3 h-3 mr-1" /> Comment est calculé ce score ?
          </motion.button>
          
          {showTooltip && (
            <motion.div 
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 text-white text-xs rounded-lg p-3 w-72 z-10 shadow-xl"
              style={{ backgroundColor: '#1f1f1f' }}
            >
              <div className="text-left">
                <p className="mb-1 font-medium">Score officiel calculé par l'outil Google PageSpeed Insights de Google.</p>
                <p className="mb-1">Ce score est calculé en combinant plusieurs facteurs de performance dont:</p>
                <ul className="list-disc pl-4 text-xs">
                  <li>Temps de chargement</li>
                  <li>Interactivité</li>
                  <li>Stabilité visuelle</li>
                  <li>Optimisation des ressources</li>
                </ul>
                <div className="h-2 w-2 rotate-45 absolute -bottom-1 left-1/2 transform -translate-x-1/2" style={{ backgroundColor: '#1f1f1f' }}></div>
              </div>
            </motion.div>
          )}
        </div>
        
        {/* Métriques additionnelles */}
        <div className="mt-2 pt-3 border-t grid grid-cols-2 gap-3 text-sm">
          <motion.div 
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col items-center p-2 rounded-lg"
            style={{ backgroundColor: '#fef3e2' }}
          >
            <span className="mb-1 flex items-center" style={{ color: '#6b7280' }}>
              <Zap className="w-3 h-3 mr-1" style={{ color: '#eb811c' }} /> Accessibilité
            </span>
            <motion.span 
              whileHover={{ scale: 1.1 }}
              className="font-medium"
              style={{ color: '#eb811c' }}
            >
              {pagespeedData ? Math.round(pagespeedData.accessibility_score * 100) : 80}%
            </motion.span>
          </motion.div>
          
          <motion.div 
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="flex flex-col items-center p-2 rounded-lg"
            style={{ backgroundColor: '#fef3e2' }}
          >
            <span className="mb-1 flex items-center" style={{ color: '#6b7280' }}>
              <Award className="w-3 h-3 mr-1" style={{ color: '#eb811c' }} /> SEO
            </span>
            <motion.span 
              whileHover={{ scale: 1.1 }}
              className="font-medium"
              style={{ color: '#eb811c' }}
            >
              {pagespeedData ? Math.round(pagespeedData.seo_score * 100) : 77}%
            </motion.span>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default PageSpeedBox;
